import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, getToken, setToken } from "../../lib/api";
import { Logo } from "../../components/site/Logo";
import LeadsTab from "./LeadsTab";
import RulesTab from "./RulesTab";
import ForfaitsTab from "./ForfaitsTab";
import ResourcesTab from "./ResourcesTab";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-[#EDE3D9] bg-[#FFFDFB] p-5">
    <p className="text-xs uppercase tracking-widest text-[#242020]/60">{label}</p>
    <p className="mt-2 font-display text-3xl text-[#650D23]" data-testid={testid}>{value}</p>
  </div>
);

export default function Admin() {
  const nav = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [forfaits, setForfaits] = useState([]);
  const [integrations, setIntegrations] = useState(null);

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/connexion");
      return;
    }
    api.get("/auth/me", { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setAdmin(r.data))
      .catch(() => { setToken(null); nav("/admin/connexion"); });
    api.get("/forfaits").then((r) => setForfaits(r.data));
    api.get("/admin/integrations").then((r) => setIntegrations(r.data)).catch(() => {});
    loadStats();
  }, [nav, loadStats]);

  if (!admin) return <div className="min-h-screen bg-[#FAF6F1]" />;
  const name = (slug) => forfaits.find((f) => f.slug === slug)?.name || "Consultation requise";
  const top = stats && Object.entries(stats.by_forfait).filter(([k]) => k !== "aucun").sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="min-h-screen bg-[#FAF6F1]" data-testid="admin-dashboard">
      <header className="border-b border-[#EDE3D9] bg-[#FFFDFB]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-4"><Logo /><span className="hidden text-sm text-[#242020]/60 sm:inline">Administration</span></div>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden sm:inline" data-testid="admin-email">{admin.email}</span>
            <button onClick={() => { setToken(null); nav("/admin/connexion"); }} className="btn-outline !py-2" data-testid="admin-logout"><LogOut className="h-4 w-4" />Déconnexion</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl space-y-8 px-5 py-8 sm:px-8">
        {stats && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Questionnaires complétés" value={stats.questionnaires} testid="stat-questionnaires" />
            <Stat label="Demandes de consultation" value={stats.consultations} testid="stat-consultations" />
            <Stat label="Nouveaux prospects" value={stats.by_status.nouveau || 0} testid="stat-nouveaux" />
            <Stat label="Forfait le plus suggéré" value={top ? `${top[0] === "aucun" ? "Aucun" : name(top[0])} (${top[1]})` : "—"} testid="stat-top-forfait" />
          </div>
        )}
        {integrations && (
          <div className="flex flex-wrap gap-3 text-xs" data-testid="admin-integrations">
            <span className="rounded-full bg-[#2D6A4F]/10 px-3 py-1.5 text-[#2D6A4F]">Notifications courriel actives → {integrations.email.recipient}</span>
            <span className="rounded-full bg-[#C97A14]/10 px-3 py-1.5 text-[#8a530c]">{integrations.ghl.message}</span>
          </div>
        )}
        <Tabs defaultValue="leads">
          <TabsList className="h-auto flex-wrap bg-[#EDE3D9]/70 p-1">
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Prospects</TabsTrigger>
            <TabsTrigger value="rules" data-testid="admin-tab-rules">Moteur de règles</TabsTrigger>
            <TabsTrigger value="forfaits" data-testid="admin-tab-forfaits">Forfaits</TabsTrigger>
            <TabsTrigger value="resources" data-testid="admin-tab-resources">Ressources</TabsTrigger>
          </TabsList>
          <TabsContent value="leads" className="mt-6"><LeadsTab forfaits={forfaits} forfaitName={name} onChange={loadStats} /></TabsContent>
          <TabsContent value="rules" className="mt-6"><RulesTab forfaits={forfaits} forfaitName={name} /></TabsContent>
          <TabsContent value="forfaits" className="mt-6"><ForfaitsTab forfaits={forfaits} onSaved={setForfaits} /></TabsContent>
          <TabsContent value="resources" className="mt-6"><ResourcesTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
